/**
 * Timeline Section
 * Chronological journey of achievements, badges, quests, and level-ups
 */

import type { TimelineItem } from "@/types/tisa-coins";
import { formatDistanceToNow } from "date-fns";

interface TimelineSectionProps {
  timeline: TimelineItem[];
}

export function TimelineSection({ timeline }: TimelineSectionProps) {
  // Show latest 10 events
  const displayedTimeline = timeline.slice(0, 10);

  const getTimelineItemColor = (type: TimelineItem["type"]) => {
    switch (type) {
      case "badge":
        return {
          bg: "bg-purple-900/30",
          border: "border-purple-500/50",
          icon: "text-purple-400",
        };
      case "quest":
        return {
          bg: "bg-blue-900/30",
          border: "border-blue-500/50",
          icon: "text-blue-400",
        };
      case "level_up":
        return {
          bg: "bg-emerald-900/30",
          border: "border-emerald-500/50",
          icon: "text-emerald-400",
        };
      case "achievement":
        return {
          bg: "bg-yellow-900/30",
          border: "border-yellow-500/50",
          icon: "text-yellow-400",
        };
      case "project":
        return {
          bg: "bg-cyan-900/30",
          border: "border-cyan-500/50",
          icon: "text-cyan-400",
        };
      default:
        return {
          bg: "bg-gray-900/30",
          border: "border-gray-500/50",
          icon: "text-gray-400",
        };
    }
  };

  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return "recently";
    }
  };

  return (
    <div className="bg-gray-900/50 border-2 border-purple/30 rounded-xl p-6">
      <h2 className="text-xl font-cinzel font-bold text-purple mb-4 flex items-center gap-2">
        📈 Your Journey
      </h2>

      {displayedTimeline.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <div className="text-4xl mb-2">🚀</div>
          <p>Your BizBook journey starts now!</p>
          <p className="text-sm mt-1">
            Complete quests and earn badges to build your story.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {displayedTimeline.map((item) => {
            const colors = getTimelineItemColor(item.type);

            return (
              <div
                key={item.id}
                className={`${colors.bg} border-2 ${colors.border} rounded-lg p-4 hover:scale-[1.02] transition-transform`}
              >
                <div className="flex items-start gap-3">
                  {/* Icon */}
                  <div className={`text-3xl flex-shrink-0 ${colors.icon}`}>
                    {item.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-semibold font-poppins">
                      {item.title}
                    </h3>
                    <p className="text-sm text-gray-300 mt-1 leading-relaxed">
                      {item.description}
                    </p>

                    {/* Metadata */}
                    {item.metadata && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {item.metadata.tier && (
                          <span className="text-xs px-2 py-1 bg-purple/20 border border-purple/30 rounded-full text-purple-300 uppercase font-bold">
                            {item.metadata.tier}
                          </span>
                        )}
                        {item.metadata.xp && (
                          <span className="text-xs px-2 py-1 bg-emerald/20 border border-emerald/30 rounded-full text-emerald-300 font-bold">
                            +{item.metadata.xp} XP
                          </span>
                        )}
                        {item.metadata.level && (
                          <span className="text-xs px-2 py-1 bg-gold/20 border border-gold/30 rounded-full text-gold-300 font-bold">
                            Level {item.metadata.level}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Time */}
                    <p className="text-xs text-gray-500 mt-2">
                      {formatTimeAgo(item.date)}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {timeline.length > 10 && (
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-400">
            Showing 10 of {timeline.length} events
          </p>
        </div>
      )}
    </div>
  );
}
